/**
 * 2026-08-06 — Effective RBAC permissions hook.
 *
 * Fetches the aggregated permissions for the current user from
 *   GET /api/instructors/me/effective-permissions
 * (see backend/routes/instructor_rbac.py) and exposes them for use
 * by the sidebar + feature-gating layer.
 *
 * The bug this fixes: previously the sidebar in MainLayout.js gated
 * items like "AI Labs", "Industry Labs", "Analytics", "Background
 * Jobs", "Announcements" on `user.role === 'Instructor'` alone. Any
 * instructor with a single cohort assignment — regardless of what
 * permissions were granted on it — would see the full toolbelt in
 * the sidebar and be able to attempt actions they had no grant for.
 *
 * Now: SuperAdmin and TenantAdmin get all-true (they have full
 * access by role). Instructors see the aggregated highest access
 * level they hold on ANY of their assigned cohorts. Instructors with
 * zero assignments get an all-none map → the sidebar hides the
 * privileged sections entirely.
 *
 * Shape returned:
 *   {
 *     loading: bool,
 *     role: str,
 *     isSuperAdmin: bool,
 *     isTenantAdmin: bool,
 *     hasAnyAssignment: bool,
 *     any: { "content.edit": bool, "submissions.review": bool, ... },
 *     perms: { "content.edit": "complete"|"partial"|"none", ... },
 *     can: (permKey) => bool  // convenience — same as any[permKey]
 *   }
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_PERMS = {
  'content.view': 'none',
  'content.edit': 'none',
  'content.publish': 'none',
  'submissions.review': 'none',
  'feedback.give': 'none',
  'grades.view': 'none',
  'grades.edit': 'none',
  'grades.finalize': 'none',
  'learner.message': 'none',
  'cohort.manage': 'none',
  'panel.participate': 'none',
  'analytics.view': 'none',
};

function buildAnyMap(perms) {
  const out = {};
  Object.keys(perms || {}).forEach((k) => {
    out[k] = perms[k] && perms[k] !== 'none';
  });
  return out;
}

export function useEffectivePermissions() {
  const { user, token } = useAuth() || {};
  const [state, setState] = useState({
    loading: true,
    role: null,
    isSuperAdmin: false,
    isTenantAdmin: false,
    hasAnyAssignment: false,
    perms: EMPTY_PERMS,
    any: buildAnyMap(EMPTY_PERMS),
    assignedCohortIds: [],
  });

  useEffect(() => {
    if (!user || !token) {
      // Not logged in yet — leave loading true so the sidebar renders
      // its skeleton state.
      return;
    }

    let cancelled = false;
    axios.get(`${API}/instructors/me/effective-permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (cancelled) return;
      const d = r?.data || {};
      const perms = { ...EMPTY_PERMS, ...(d.perms || {}) };
      setState({
        loading: false,
        role: d.role || user?.role || null,
        isSuperAdmin: !!d.isSuperAdmin,
        isTenantAdmin: !!d.isTenantAdmin,
        hasAnyAssignment: !!d.hasAnyAssignment,
        perms,
        any: d.any || buildAnyMap(perms),
        assignedCohortIds: d.assignedCohortIds || [],
      });
    }).catch(() => {
      if (cancelled) return;
      // Fail closed — treat as no permissions rather than accidentally
      // showing every menu item on a network blip.
      setState((s) => ({ ...s, loading: false }));
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id, token]);

  const can = useMemo(
    () => (permKey) => !!state.any?.[permKey],
    [state.any],
  );

  return { ...state, can };
}
